import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Card, Col, Row, Typography } from 'antd';
import { debounce } from 'lodash';
import { useAuth } from '../../contexts/AuthContext';
import * as whatsappService from '../../services/whatsappService';
import { WAAssignPayload, WAConversation, WAMessage, WASendPayload } from '../../types/whatsapp';
import ConversationList, { WAFilter } from './ConversationList';
import MessageThread from './MessageThread';
import AssignModal from './AssignModal';

const { Title } = Typography;

const POLL_INTERVAL_MS = 15000; // fallback; real-time via SSE (/wa/stream)

const Inbox: React.FC = () => {
  const { hasPermission } = useAuth();
  const { message } = App.useApp();

  const canAssign = hasPermission('wa.assign');
  const canSend = hasPermission('wa.send');

  const [filter, setFilter] = useState<WAFilter>('mine');
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState<WAConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  const [selectedConversation, setSelectedConversation] = useState<WAConversation | null>(null);
  const [messages, setMessages] = useState<WAMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedConversation?.id || null;

  // refs so the SSE effect can call the latest fetchers without re-subscribing
  const fetchConvRef = useRef<(silent?: boolean) => void>(() => {});
  const fetchMsgRef = useRef<(id: string, silent?: boolean) => void>(() => {});

  const fetchConversations = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingConversations(true);
      try {
        const res = await whatsappService.listConversations({ filter, q: search || undefined, page: 1 });
        const list: WAConversation[] = res.data?.data || [];
        setConversations(list);

        // keep the selected conversation's unread badge / window state in sync
        if (selectedIdRef.current) {
          const updated = list.find((c) => c.id === selectedIdRef.current);
          if (updated) {
            setSelectedConversation((prev) => (prev ? { ...prev, ...updated } : prev));
          }
        }
      } catch (error) {
        console.error(error);
        if (!silent) message.error('Gagal memuat daftar percakapan');
      } finally {
        if (!silent) setLoadingConversations(false);
      }
    },
    [filter, search, message]
  );

  const fetchMessages = useCallback(
    async (conversationId: string, silent = false) => {
      if (!silent) setLoadingMessages(true);
      try {
        const res = await whatsappService.getMessages(conversationId, 1);
        setMessages(res.data?.data || []);
      } catch (error) {
        console.error(error);
        if (!silent) message.error('Gagal memuat pesan');
      } finally {
        if (!silent) setLoadingMessages(false);
      }
    },
    [message]
  );

  // initial + filter/search driven fetch of the conversation list
  useEffect(() => {
    fetchConversations(false);
  }, [fetchConversations]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 400),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSelect = useCallback(
    async (conversation: WAConversation) => {
      setSelectedConversation(conversation);
      await fetchMessages(conversation.id, false);
      try {
        await whatsappService.markRead(conversation.id);
        setConversations((prev) =>
          prev.map((c) => (c.id === conversation.id ? { ...c, unread_count: 0 } : c))
        );
        setSelectedConversation((prev) => (prev ? { ...prev, unread_count: 0 } : prev));
      } catch (error) {
        console.error(error);
      }
    },
    [fetchMessages]
  );

  // keep refs current for the SSE effect
  fetchConvRef.current = fetchConversations;
  fetchMsgRef.current = fetchMessages;

  // polling fallback: refresh conversation list + open thread (SSE handles live)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations(true);
      if (selectedIdRef.current) {
        fetchMessages(selectedIdRef.current, true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchMessages, selectedConversation?.id]);

  // SSE: real-time inbox — push inbound messages/conversations instantly
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const base = (process.env.REACT_APP_BASE_URL || '/api/v1').replace(/\/$/, '');
    const url = `${base}/wa/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.onmessage = (ev) => {
      let data: any = null;
      try {
        data = JSON.parse(ev.data);
      } catch {
        return;
      }
      // refresh list (new/updated conversation, unread badges)
      fetchConvRef.current(true);
      // if the event belongs to the open thread, refresh its messages
      const openId = selectedIdRef.current;
      if (openId && data?.conversation_id === openId) {
        fetchMsgRef.current(openId, true);
      }
    };
    es.onerror = () => {
      // EventSource auto-reconnects; nothing to do here
    };
    return () => es.close();
  }, []);

  const handleSend = useCallback(
    async (payload: WASendPayload) => {
      if (!selectedConversation) return;
      try {
        await whatsappService.sendMessage(selectedConversation.id, payload);
        await fetchMessages(selectedConversation.id, true);
        await fetchConversations(true);
      } catch (error: any) {
        console.error(error);
        if (error?.response?.status === 409) {
          message.error('Jendela 24 jam tertutup — balasan via template tersedia di tahap berikutnya');
        } else {
          message.error('Gagal mengirim pesan');
        }
        throw error;
      }
    },
    [selectedConversation, fetchMessages, fetchConversations, message]
  );

  const handleTemplateSent = useCallback(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id, false);
    }
    fetchConversations(true);
  }, [selectedConversation, fetchMessages, fetchConversations]);

  const handleAssignSubmit = useCallback(
    async (payload: WAAssignPayload) => {
      if (!selectedConversation) return;
      try {
        await whatsappService.assignConversation(selectedConversation.id, payload);
        message.success('Percakapan berhasil di-assign');
        setAssignModalOpen(false);
        await fetchConversations(false);
      } catch (error) {
        console.error(error);
        message.error('Gagal melakukan assign percakapan');
      }
    },
    [selectedConversation, fetchConversations, message]
  );

  return (
    <div style={{ height: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column' }}>
      <Title level={2} style={{ margin: '0 0 16px' }}>
        WhatsApp Inbox
      </Title>
      <Row gutter={16} style={{ flex: 1, minHeight: 0 }}>
        <Col xs={24} md={8} style={{ height: '100%' }}>
          <Card bordered={false} styles={{ body: { height: '100%', padding: 12 } }} style={{ height: '100%' }}>
            <ConversationList
              conversations={conversations}
              loading={loadingConversations}
              selectedId={selectedConversation?.id || null}
              filter={filter}
              canAssign={canAssign}
              search={search}
              onFilterChange={(f) => setFilter(f)}
              onSearchChange={debouncedSetSearch}
              onSelect={handleSelect}
            />
          </Card>
        </Col>
        <Col xs={24} md={16} style={{ height: '100%' }}>
          <Card bordered={false} styles={{ body: { height: '100%', padding: 16 } }} style={{ height: '100%' }}>
            <MessageThread
              conversation={selectedConversation}
              messages={messages}
              loadingMessages={loadingMessages}
              canSend={canSend}
              canAssign={canAssign}
              onSend={handleSend}
              onAssignClick={() => setAssignModalOpen(true)}
              onTemplateSent={handleTemplateSent}
            />
          </Card>
        </Col>
      </Row>

      <AssignModal
        open={assignModalOpen}
        conversation={selectedConversation}
        onCancel={() => setAssignModalOpen(false)}
        onSubmit={handleAssignSubmit}
      />
    </div>
  );
};

export default Inbox;
